import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';
import AuthCardLeft from './AuthCardLeft';
import MobileDesktopOnlyModal from './MobileDesktopOnlyModal';
import PasswordField from '../common/PasswordField';
import './Auth.css';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { updateUserPassword } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { text: 'At least 6 characters', minLength: 6 },
    { text: 'At least 1 uppercase letter', regex: /[A-Z]/ },
    { text: 'At least 1 lowercase letter', regex: /[a-z]/ },
    { text: 'At least 1 number', regex: /\d/ }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('Current password is required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await updateUserPassword(newPassword, currentPassword);

      // Navigate back to dashboard with success message
      navigate('/dashboard', {
        state: { successMessage: 'Your profile has been updated.' }
      });
    } catch (err: any) {
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later');
      } else {
        setError('Failed to change password. Please try again.');
      }
      console.error('Change password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <MobileDesktopOnlyModal />
      <Card className="auth-card">
        <Card.Body>
          <AuthCardLeft />
          <div className="auth-card-right">
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <h1 className="text-center">Reset your password</h1>
              <h2 className="text-muted mb-4">Enter your current password and choose a new password</h2>

              {error && <div className="text-danger text-center mb-3">{error}</div>}

              <Form onSubmit={handleSubmit} noValidate>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    disabled={loading}
                    style={{ paddingRight: '45px' }}
                  />
                  <Button
                    variant="link"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={loading}
                    type="button"
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      padding: '5px',
                      color: 'var(--color-text-secondary)',
                      zIndex: 10
                    }}
                  >
                    {showCurrentPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </Button>
                </div>
              </Form.Group>

              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Create a secure password"
                disabled={loading}
                requirements={passwordRequirements}
                strengthLengthBonus={10}
              />

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Must match password above"
                    required
                    disabled={loading}
                    minLength={6}
                    style={{ paddingRight: '45px' }}
                  />
                  <Button
                    variant="link"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                    type="button"
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      padding: '5px',
                      color: 'var(--color-text-secondary)',
                      zIndex: 10
                    }}
                  >
                    {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </Button>
                </div>
              </Form.Group>

              <div className="d-flex gap-3">
                <Button
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  className="flex-fill"
                  type="submit"
                >
                  {loading ? 'CHANGING PASSWORD...' : 'CHANGE PASSWORD'}
                </Button>
              </div>
            </Form>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChangePassword;
